// "Biggest Moment" — a leverage HEURISTIC over at-bat data.
//
// IMPORTANT (honesty): this is NOT literal Win Probability Added. True WPA needs
// the score margin and base/out state at every plate appearance; our scorebook
// import reliably stores neither (`baseOutStateAtEntry` exists in the type but is
// "best-effort" and is not populated by the demo data or the current import
// pipeline). So we compute a defensible LEVERAGE score from the fields that ARE
// always present — inning, result, RBI, runners scored, leadoff — and the UI
// brands it "Biggest Moment" / "Clutch", never "WPA". A footnote explains how we
// measure it.
//
// The model: impact = run production (RBI + runners scored, with a floor for a
// big hit that didn't happen to score anyone) × a late-inning leverage multiplier
// (a 6th-inning hit matters more than a 1st-inning hit) × an extra-base bonus.
// When `baseOutStateAtEntry` IS present we add a small bonus for cashing in a
// high-leverage base-out state, but we never depend on it.

import type { AtBatDoc } from '../../types/dugout';

export interface MomentPick {
  playerId?: string;
  playerName: string;
  gameId: string;
  inning: number;
  resultCode: string;
  rbi: number;
  runnersScored: number;
  /** The computed leverage score (relative, unitless). Higher = bigger moment. */
  score: number;
  /** Human description built deterministically, e.g. "2-run double in the 6th". */
  description: string;
}

export interface ClutchPick {
  playerId?: string;
  playerName: string;
  /** Sum of positive-leverage scores across the season. */
  total: number;
  moments: number;          // count of contributing at-bats
}

export interface MoundLeveragePick {
  playerId?: string;
  playerName: string;
  gameId: string;
  inning: number;
  /** Number of runners the pitcher stranded in the highest-leverage spot found. */
  stranded: number;
  description: string;
}

export interface BiggestMomentResult {
  biggest: MomentPick | null;
  clutchPlayer: ClutchPick | null;
  topLeverageInning: MoundLeveragePick | null;
}

const HIT_CODES = new Set(['1B', '2B', '3B', 'HR']);
const EXTRA_BASE = new Set(['2B', '3B', 'HR']);

/** Late innings carry more weight. Caps so a 9th-inning blowout AB isn't absurd. */
function inningLeverage(inning: number): number {
  if (inning <= 1) return 1.0;
  if (inning === 2) return 1.1;
  if (inning === 3) return 1.25;
  if (inning === 4) return 1.45;
  if (inning === 5) return 1.7;
  if (inning === 6) return 2.0;   // typical youth regulation final inning
  return 2.3;                      // extras / 7th+
}

function extraBaseBonus(code: string): number {
  if (code === 'HR') return 1.6;
  if (code === '3B') return 1.35;
  if (code === '2B') return 1.2;
  return 1.0;
}

/** Tiny bonus when we happen to know the runners-on / outs state. Optional. */
function baseOutBonus(state?: string): number {
  if (!state) return 1.0;
  // Loose parse: count runners encoded in the state string (e.g. "1-2-3 / 2 out",
  // "_23/1", etc.). We only need "more runners + more outs = higher leverage".
  const runners = (state.match(/[123]/g) || []).length;
  const outsMatch = state.match(/(\d)\s*out/i);
  const outs = outsMatch ? parseInt(outsMatch[1]) : (state.includes('2') ? 0 : 0);
  // Up to ~+35% when bases are loaded with two outs.
  return 1 + Math.min(runners, 3) * 0.07 + Math.min(outs, 2) * 0.07;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const RESULT_NOUN: Record<string, string> = {
  '1B': 'single', '2B': 'double', '3B': 'triple', 'HR': 'home run',
  BB: 'walk', HBP: 'hit by pitch', FC: "fielder's choice", E: 'reach on error',
};

function describeMoment(ab: AtBatDoc): string {
  const noun = RESULT_NOUN[ab.resultCode] || ab.resultCode.toLowerCase();
  const runs = ab.rbi || ab.runnersScored || 0;
  const runLabel = runs >= 1 ? `${runs}-run ` : '';
  return `${runLabel}${noun} in the ${ordinal(ab.inning)}`;
}

/**
 * Score one of OUR at-bats for offensive leverage. Returns 0 for non-events
 * (outs that produced nothing) so they don't pollute the clutch totals.
 */
export function scoreOffensiveAtBat(ab: AtBatDoc): number {
  const runsProduced = (ab.rbi || 0) + Math.max(0, (ab.runnersScored || 0) - (ab.rbi || 0));
  const isHit = HIT_CODES.has(ab.resultCode);
  const reached = ab.reachedBase || isHit || ab.resultCode === 'BB' || ab.resultCode === 'HBP';

  // Base impact: every run produced is worth 1; a hit that didn't score anyone
  // still gets a small floor (it created a chance); a walk/HBP a smaller one.
  let impact = runsProduced;
  if (impact === 0) {
    if (EXTRA_BASE.has(ab.resultCode)) impact = 0.6;
    else if (ab.resultCode === '1B') impact = 0.35;
    else if (reached) impact = 0.15;
    else return 0; // an out with nothing → not a "moment"
  }

  return impact
    * inningLeverage(ab.inning)
    * extraBaseBonus(ab.resultCode)
    * baseOutBonus(ab.baseOutStateAtEntry);
}

/**
 * Compute the season's biggest offensive moment, the clutch leader, and the
 * highest-leverage inning a pitcher escaped.
 *
 * @param atBats  ALL at-bats across the season (each tagged with gameId).
 * @param rosterNameById  authoritative roster names (so we show "Marcus Rivera",
 *                        not the scorebook's abbreviation).
 */
export function computeBiggestMoments(
  atBats: AtBatDoc[],
  rosterNameById: Map<string, string> = new Map(),
): BiggestMomentResult {
  let biggest: MomentPick | null = null;
  const clutchTotals = new Map<string, { name: string; total: number; moments: number }>();

  // ── Offense: our batters ──────────────────────────────────────────────────
  for (const ab of atBats) {
    if (ab.batterTeam !== 'us') continue;
    const score = scoreOffensiveAtBat(ab);
    if (score <= 0) continue;

    const name = (ab.batterId && rosterNameById.get(ab.batterId)) || ab.batterName;

    // Track the single biggest moment.
    if (!biggest || score > biggest.score) {
      biggest = {
        playerId: ab.batterId,
        playerName: name,
        gameId: ab.gameId,
        inning: ab.inning,
        resultCode: ab.resultCode,
        rbi: ab.rbi || 0,
        runnersScored: ab.runnersScored || 0,
        score,
        description: describeMoment(ab),
      };
    }

    // Accumulate clutch totals (only positive-leverage at-bats contribute).
    if (ab.batterId) {
      const cur = clutchTotals.get(ab.batterId) || { name, total: 0, moments: 0 };
      cur.total += score;
      cur.moments += 1;
      clutchTotals.set(ab.batterId, cur);
    }
  }

  // ── Clutch leader: highest cumulative positive leverage (min 2 moments so a
  //    single big hit doesn't crown a part-time bat). ─────────────────────────
  let clutchPlayer: ClutchPick | null = null;
  for (const [pid, v] of clutchTotals) {
    if (v.moments < 2) continue;
    if (!clutchPlayer || v.total > clutchPlayer.total) {
      clutchPlayer = { playerId: pid, playerName: v.name, total: v.total, moments: v.moments };
    }
  }
  // If nobody cleared the 2-moment bar, fall back to the best single contributor.
  if (!clutchPlayer && clutchTotals.size > 0) {
    for (const [pid, v] of clutchTotals) {
      if (!clutchPlayer || v.total > clutchPlayer.total) {
        clutchPlayer = { playerId: pid, playerName: v.name, total: v.total, moments: v.moments };
      }
    }
  }

  // ── Mound: highest-leverage inning one of our pitchers escaped ─────────────
  // Heuristic: within each (game, inning, our pitcher), count how many opponent
  // batters reached vs how the inning resolved. A pitcher who faced a loaded,
  // late inning and stranded runners scores highest. We approximate "stranded"
  // as (reached on base that inning) - (runs that inning), weighted by inning.
  type Bucket = { pid?: string; name: string; reached: number; runs: number; batters: number };
  const innings = new Map<string, Bucket>();
  for (const ab of atBats) {
    if (ab.pitcherTeam !== 'us') continue;
    const key = `${ab.gameId}|${ab.inning}|${ab.pitcherId ?? ab.pitcherName}`;
    const name = (ab.pitcherId && rosterNameById.get(ab.pitcherId)) || ab.pitcherName || 'Our pitcher';
    const b = innings.get(key) || { pid: ab.pitcherId, name, reached: 0, runs: 0, batters: 0 };
    b.batters += 1;
    if (ab.reachedBase) b.reached += 1;
    b.runs += ab.runnersScored || 0;
    innings.set(key, b);
  }

  let topLeverageInning: MoundLeveragePick | null = null;
  let bestMound = 0;
  for (const [key, b] of innings) {
    const stranded = Math.max(0, b.reached - b.runs);
    if (stranded < 1) continue; // no traffic stranded → not a leverage escape
    const inning = parseInt(key.split('|')[1]) || 1;
    // Escapes are most impressive when runners were stranded AND few/no runs scored.
    const cleanliness = b.runs === 0 ? 1.5 : 1.0;
    const mScore = stranded * inningLeverage(inning) * cleanliness;
    if (mScore > bestMound) {
      bestMound = mScore;
      topLeverageInning = {
        playerId: b.pid,
        playerName: b.name,
        gameId: key.split('|')[0],
        inning,
        stranded,
        description: `stranded ${stranded} in the ${ordinal(inning)}${b.runs === 0 ? ', no runs' : ''}`,
      };
    }
  }

  return { biggest, clutchPlayer, topLeverageInning };
}
